import { getErrorMessage, useServices, useToast } from '@/app/providers';
import { Loading } from '@/core/ui/components/Loading';
import { useFileUpload, useRoleLabel } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { User as UserEntity } from '@/shared/types';
import { Camera, Edit2, Key, Loader2, Lock, Mail, Phone, Save, ShieldCheck, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { getInitials } from '@yawi/infrastructure';

export const StaffProfile = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { userRepo, setCurrentUser } = useServices();
  const { getLabel } = useRoleLabel();

  const [user, setUser] = useState<UserEntity | null>(null);
  const [loading, setLoading] = useState(false);

  // Edit states
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // File upload hook
  const { upload: uploadAvatar, isUploading: isUploadingAvatar } = useFileUpload('avatars', {
    allowedTypes: ['image/*'],
  });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const u = await userRepo.getCurrentUser();
    setUser(u);
    setPhoneValue(u.phone || '');
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && user) {
      try {
        const result = await uploadAvatar(file);
        if (result) {
          const updatedUser = { ...user, avatarUrl: result.url, avatarFileId: result.id };
          await userRepo.updateProfile({
            avatarUrl: result.url,
            avatarFileId: result.id,
          });
          setUser(updatedUser);
          setCurrentUser(updatedUser);
          toast.success(t('profile.avatar.updated'));
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleSavePhone = async () => {
    if (!user) return;
    setLoading(true);
    try {
      await userRepo.updateProfile({ phone: phoneValue });
      setUser({ ...user, phone: phoneValue });
      setIsEditingPhone(false);
      toast.success(t('profile.phone.updated'));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('profile.password.mismatch'));
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error(t('profile.password.tooShort'));
      return;
    }

    setLoading(true);
    try {
      await userRepo.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setIsChangingPassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success(t('profile.password.changed'));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveRole = () => {
    if (!user) return '';
    return (user as any).tenantRole || user.role;
  };

  if (!user) return <Loading />;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">

      {/* Header Card */}
      <div className="bg-surface rounded-2xl shadow-sm border border-theme p-6 md:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 dark:bg-primary-900/20 rounded-full -mr-20 -mt-20 blur-3xl opacity-50"></div>

        <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
          {/* Avatar with visible ring */}
          <div className="relative shrink-0">
            <div className="absolute inset-0 rounded-full">
              <div className="w-full h-full rounded-full"></div>
            </div>
            <div className="relative w-20 h-20 rounded-full overflow-hidden shadow-md group cursor-pointer mt-[2px] ml-[2px]">
              {isUploadingAvatar ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : user.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 text-white text-xl font-bold">
                  {getInitials(user.name)}
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer rounded-full">
                <Camera className="w-5 h-5 text-white" />
                <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} />
              </label>
            </div>
          </div>

          {/* Identity Block */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-semibold text-gray-900 text-theme tracking-tight">{user.name}</h2>
            
            {/* Colorful Badge System */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-4">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-purple-100 dark:bg-purple-500/20 text-purple-500 shadow-sm">
                <Key size={12} />
                <span>{getLabel(getEffectiveRole())}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-green-100 dark:bg-green-500/20 text-green-700 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span>{t(`profile.status.${user.status}`)}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information Card */}
      <div className="bg-surface rounded-xl shadow-sm border border-theme p-6">
        <h3 className="text-sm font-bold text-theme-secondary uppercase tracking-wider mb-4">
          {t('profile.contact.title')}
        </h3>

        <div className="space-y-4">
          {/* Email (Read-only) */}
          <div className="flex items-center gap-4 p-4 bg-surface-elevated rounded-xl border border-theme">
            <div className="p-2 bg-surface rounded-lg shadow-sm text-blue-600 dark:text-blue-400">
              <Mail size={20}/>
            </div>
            <div className="flex-1">
              <p className="text-xs text-theme-secondary font-bold">Email</p>
              <p className="text-theme font-medium">{user.email}</p>
            </div>
          </div>

          {/* Phone (Editable) */}
          <div className="flex items-center gap-4 p-4 bg-surface-elevated rounded-xl border border-theme">
            <div className="p-2 bg-surface rounded-lg shadow-sm text-emerald-600 dark:text-emerald-400">
              <Phone size={20}/>
            </div>
            <div className="flex-1">
              <p className="text-xs text-theme-secondary font-bold">{t('profile.phone.label')}</p>
              {isEditingPhone ? (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="tel"
                    value={phoneValue}
                    onChange={(e) => setPhoneValue(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-theme rounded-lg text-sm bg-surface text-theme"
                    autoFocus
                  />
                  <button
                    onClick={handleSavePhone}
                    disabled={loading}
                    className="p-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  </button>
                  <button
                    onClick={() => { setIsEditingPhone(false); setPhoneValue(user.phone || ''); }}
                    className="p-1.5 bg-surface-elevated text-theme-secondary rounded-lg hover:bg-surface-elevated"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-theme font-medium">
                    {user.phone || t('profile.phone.notRegistered')}
                  </p>
                  <button
                    onClick={() => setIsEditingPhone(true)}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-800 text-xs font-bold flex items-center gap-1"
                  >
                    <Edit2 size={14}/> {t('common.edit')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Security Card */}
      <div className="bg-surface rounded-xl shadow-sm border border-theme p-6">
        <h3 className="text-sm font-bold text-theme-secondary uppercase tracking-wider mb-4">
          {t('profile.security.title')}
        </h3>

        {isChangingPassword ? (
          <form onSubmit={handleChangePassword} className="space-y-4 animate-in fade-in">
            <Input
              label={t('profile.password.current')}
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
              required
            />
            <Input
              label={t('profile.password.new')}
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
              required
            />
            <Input
              label={t('profile.password.confirm')}
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
              required
            />
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                isLoading={loading}
                icon={<Save size={14}/>}
              >
                {t('common.save')}
              </Button>
            </div>
          </form>
        ) : (
          <div
            onClick={() => setIsChangingPassword(true)}
            className="flex items-center gap-4 p-4 bg-surface-elevated rounded-xl border border-theme cursor-pointer hover:bg-surface-elevated transition-colors"
          >
            <div className="p-2 bg-surface rounded-lg shadow-sm text-orange-600 dark:text-orange-400">
              <Lock size={20}/>
            </div>
            <div className="flex-1">
              <p className="text-theme font-medium">{t('profile.password.change')}</p>
              <p className="text-xs text-theme-secondary">••••••••</p>
            </div>
            <Edit2 size={16} className="text-theme-secondary" />
          </div>
        )}
      </div>
    </div>
  );
};
